import { useCallback } from "react";
import { useIntl } from "react-intl";
import { useToast } from "@chakra-ui/react";
import { useHistory } from "react-router-dom";
import { useSessionJWT, useSessionCtx, inIframe, FLOWS, usePostMessage } from "utils";
import { useSettings } from "utils/settingsContext";
import { signToken } from "api/accounts-customer";
import { getPlaidLinkToken } from "api/payments";
import { BASE_DOMAIN } from "env";
import { useOverlaySpinner } from "components/OverlaySpinner";

/**
 * Asynchronously gets a token using the provided sessionJWT and sessionCtx.
 * @param {string} sessionJWT - The session JWT.
 * @param {string} sessionCtx - The session context.
 * @param {Function} debug - Optional: The debug function for logging.
 * @returns {Promise<string>} - The token obtained from signToken.
 */
const getToken = async (sessionJWT, sessionCtx, allowedDomainRedirectPatterns) => {
  // Sign the integrator's allowed redirect patterns into the state token. The
  // fullpage /ui/plaid/* route does not have a populated SettingsProvider, and a
  // server-signed token cannot be tampered with, so this is the trustworthy source
  // for the allowed patterns when validating the post-flow redirect.
  const { token } = await signToken(sessionJWT, {
    sessionCtx,
    sessionJWT,
    allowedDomainRedirectPatterns,
  });
  return token;
};

/**
 * Custom hook for generating a plaid URL and redirecting the user.
 * @returns {Object} - An object containing the function to generate URL and its state.
 */
const useGenerateUrlAndRedirectUser = () => {
  const { formatMessage } = useIntl();
  const history = useHistory();
  const toast = useToast();
  const { sessionJWT } = useSessionJWT();
  const { sessionCtx, debug, setFlowState } = useSessionCtx();
  const { integratorSettings } = useSettings();
  const { postMessage } = usePostMessage(FLOWS.PAYPAL, sessionCtx?.intCtx);
  const { showOverlaySpinner, hideOverlaySpinner, isOpen } = useOverlaySpinner();

  // Generates a URL with plaidLinkToken and stateJwt, then redirects the user.
  const generatUrlAndRedirectUser = useCallback(
    (plaidLinkToken, stateJwt) => {
      if (!plaidLinkToken || !stateJwt) return;
      const params = new URLSearchParams();
      params.set("state", stateJwt);
      params.set("plink", plaidLinkToken);
      const redirectBase = `https://web.${BASE_DOMAIN}/ui`;
      const redirectPath = `/plaid/main?${params.toString()}`;
      const fullRedirectUrl = `${redirectBase}${redirectPath}`;
      debug("plaidbutton_onclick__redirect", fullRedirectUrl);
      setFlowState("plaid.start-redirect");
      if (inIframe()) {
        postMessage(`tilia.${FLOWS.PLAID}.redirect`, { redirect: fullRedirectUrl });
      } else {
        // handle outside of iFrame
        history.push(redirectPath);
      }
    },
    [history, postMessage, setFlowState, debug]
  );

  // Handles the generation of URL and redirection of the user, with error handling.
  const onGenerateUrlAndRedirectUser = async () => {
    showOverlaySpinner();
    try {
      const stateJwt = await getToken(
        sessionJWT,
        sessionCtx,
        integratorSettings?.allowed_domain_redirect_patterns
      ); // TODO: should I make this optionally load when component loads to save a network call when button is clicked?
      const plaidLinkRes = await getPlaidLinkToken(sessionJWT);
      const plaid_link_token = plaidLinkRes.payload?.plaid_link_token;
      if (!stateJwt || !plaid_link_token) {
        throw new Error("Missing required redirect information.");
      }
      generatUrlAndRedirectUser(plaid_link_token, stateJwt);
    } catch (error) {
      const msg =
        error.message ||
        formatMessage({
          defaultMessage: "Unknown error in add bank account.",
          description: "Pop up error message",
        });
      // debug("plaidbutton_onclick__error", error);
      toast({
        title: formatMessage({
          defaultMessage: "Error",
          description: "Pop up title",
        }),
        description: msg,
        status: "error",
        isClosable: true,
      });
    }
    hideOverlaySpinner();
  };

  return { onGenerateUrlAndRedirectUser, isOpen };
};

export default useGenerateUrlAndRedirectUser;
